import { API_BASE_URL } from '../config/constants';

/**
 * Service pour gérer l'upload de fichiers
 */
class UploadService {
  /**
   * Upload un fichier audio
   * @param {File} file - Le fichier à uploader
   * @returns {Promise<Object>} Les informations du fichier uploadé
   */
  async uploadSound(file) {
    const formData = new FormData();
    formData.append('sound', file);

    const response = await fetch(`${API_BASE_URL}/uploads/sound`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Erreur lors de l'upload du fichier");
    }

    const data = await response.json();
    return data.file;
  }

  /**
   * Récupère la liste des fichiers audio uploadés
   * @returns {Promise<Array>} Liste des fichiers
   */
  async getSounds() {
    const response = await fetch(`${API_BASE_URL}/uploads/sounds`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message || 'Erreur lors de la récupération des fichiers'
      );
    }

    const data = await response.json();
    return data.files || [];
  }

  /**
   * Supprime un fichier audio
   * @param {string} filename - Nom du fichier à supprimer
   * @returns {Promise<void>}
   */
  async deleteSound(filename) {
    const response = await fetch(
      `${API_BASE_URL}/uploads/sound/${encodeURIComponent(filename)}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message || 'Erreur lors de la suppression du fichier'
      );
    }
  }

  /**
   * Construit l'URL complète d'un fichier uploadé (sons, etc.).
   * En production (navigateur / OBS), retourne une URL absolue pour éviter les problèmes
   * de résolution dans les sources navigateur OBS.
   * @param {string} relativePath - Chemin relatif du fichier (ex: /uploads/sounds/file.mp3)
   * @returns {string} URL complète (absolue en prod)
   */
  getFullUrl(relativePath) {
    if (!relativePath) return '';

    // Si c'est déjà une URL complète, la retourner telle quelle
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }

    // En navigateur (ex: OBS Sound Player), utiliser l'origine courante pour une URL absolue
    if (typeof window !== 'undefined' && window.location?.origin) {
      const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
      return `${window.location.origin}${path}`;
    }

    // Côté build / SSR : base à partir de l'API
    const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '') || '';
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
    return baseUrl ? `${baseUrl}${path}` : path;
  }
}

const uploadService = new UploadService();
export default uploadService;
